import * as jotai from 'jotai'

import * as api from '@owl-nest/api-client/next'
import * as hooks from '@owl-nest/hooks'

import * as base from './api'

const REFERRALS_CACHE = jotai.atom<hooks.Cache<api.ReferralListPage>>({})

export type UseReferralsParams = {
  limit?: number
  page?: number
  q?: string
  sort?: string
}

type UseReferralsConfig = {
  fetchOnMount?: boolean | 'force'
  params?: UseReferralsParams
  withToken?: boolean
}

export function toURLSearchParams({ limit, page, q, sort }: UseReferralsParams): URLSearchParams {
  const searchParams = new URLSearchParams()
  if (limit !== undefined) {
    searchParams.set('limit', String(limit))
  }
  if (page !== undefined) {
    searchParams.set('offset', String((page - 1) * (limit ?? 20)))
  }
  if (q !== undefined) {
    searchParams.set('q', String(q))
  }
  if (sort !== undefined) {
    searchParams.set('sort', String(sort))
  }
  return searchParams
}

export type UseReferrals = {
  response: hooks.Response<api.ReferralListPage>
  query: () => Promise<void>
}

export function useReferralsPage(
  projectSlugOrId?: number | string,
  { fetchOnMount = true, params = {}, withToken = false }: UseReferralsConfig = {},
): UseReferrals {
  const sharedQuery = hooks.useSharedQuery(
    async (projectId: number | string, params: UseReferralsParams, withToken: boolean) =>
      base.list(projectId, { withToken, getParams: toURLSearchParams(params) }),
    {
      atom: REFERRALS_CACHE,
      id,
    },
  )

  hooks.useFetchOnMount({ fetchOnMount, query, getResponse }, id(projectSlugOrId, params, withToken))

  return {
    response: getResponse(),
    query,
  }

  function getResponse(): hooks.Response<api.ReferralListPage> {
    if (projectSlugOrId === undefined) {
      return { status: hooks.QueryStatus.PRISTINE }
    }
    return sharedQuery.getCurrentResponse([projectSlugOrId, params, withToken])
  }

  async function query(): Promise<void> {
    if (projectSlugOrId === undefined) {
      return
    }

    return sharedQuery.query(projectSlugOrId, params, withToken)
  }
}

function id(projectId: number | string | undefined, params: UseReferralsParams, withToken: boolean): string {
  return JSON.stringify([projectId, params.limit, params.page, params.sort, params.q, withToken])
}
